// Core components
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';

// Third party components
import { MatDialog } from "@angular/material";
import { FieldType } from '@ngx-formly/material';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AddSupplierDialogComponent } from 'src/app/modules/suppliers/components/add-supplier-dialog/add-supplier-dialog.component';
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { HelperService } from 'src/app/shared/services/helper.service';

// Custom components

// @see https://stackblitz.com/edit/angular-g9txuh-zysdfi?file=src/app/autocomplete-type.component.ts

@Component({
  selector: 'app-formly-autocomplete-supplier-type',
  template: `
    <!-- [trackByFn]="trackByFn" -->
    <mat-label class="custom-label" [ngClass]="{'d-none': !to.customLabel }">{{ to.customLabel }}
      <span *ngIf="to.required">*</span>
    </mat-label>
    <ng-select
      [items]="options$ | async"
      [bindValue]="to.bindValue"
      [bindLabel]="to.bindLabel"
      [multiple]="to.multiple"
      [hideSelected]="to.hideSelected"
      [typeahead]="optionsInput$"
      [loading]="optionsLoading"
      [(ngModel)]="value"
      [placeholder]="to.placeholder"
      [minTermLength]="to.minTermLength"
      [typeToSearchText]="to.typeToSearchText"
      [clearAllText]="to.clearAllText"
      [addTag]="addTag.bind(this)"
      [addTagText]="to.addTagText"
      [notFoundText]="to.notFoundText"
      [loadingText]="to.loadingText"
      (change)="onChange($event)"
      [disabled]="to.disabled"
      [readonly]="to.readonly"
      [required]="to.required"
      [ngClass]="{'autocomplete-invalid': !value && to.required }"
      >
    </ng-select>
    <div *ngIf="!value && to.required" class="mat-error" style="font-size: 75%; padding: 0.4375em 0;">
      Il campo è richiesto
    </div>
  `,
})
export class AutocompleteSupplierTypeComponent extends FieldType implements OnInit, AfterViewInit, OnDestroy {
  onDestroy$ = new Subject<void>();
  search$ = new EventEmitter();
  options$: Observable<any> = new Observable();
  optionsLoading = false;
  optionsInput$: Subject<string> = new Subject<string>();
  value: any;
  
  constructor(
    public dialog: MatDialog,
    private helperService: HelperService,
  ) {
    super();
  }

  addTag = (name: string): Promise<any> => {
    return new Promise((resolve) =>{
      const dialogRef = this.dialog.open(AddSupplierDialogComponent, {
        data: {
          name
        },
        width: "900px",
      });
  
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe(
          (res) => {
            if (res && res.status) {
              this.value = res.supplier._id;

              if (this.formControl.parent.get('ragioneSocialeFornitore')) {
                this.formControl.parent.get('ragioneSocialeFornitore').setValue(res.supplier.companyName);
              } else if (this.formControl.parent.get('nomeFornitoreCombustibile')) {
                this.formControl.parent.get('nomeFornitoreCombustibile').setValue(res.supplier.companyName);
              }
              const vatNumber = res.supplier.vatNumber ? ` - ${res.supplier.vatNumber}` : '';
              const city = (res.supplier.legalAddress && res.supplier.legalAddress.city) ? ` - ${res.supplier.legalAddress.city}` : '';

              resolve({_id: res.supplier._id, companyName: res.supplier.companyName})
            }
            resolve(''); 
          },
          (err) => {
            this.helperService.handleError(err);
            resolve(''); 
          }
        );
    });

  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.value = this.formControl.value;

    // Pre-populate formField options with given values (if any)
    const initialValues = [];
    let companyName = '';

    if (
      this.formControl.parent.get('ragioneSocialeFornitore') &&
      this.formControl.parent.get('ragioneSocialeFornitore').value !== ''
    ) {
      companyName = this.formControl.parent.get('ragioneSocialeFornitore').value;
    } else if (
      this.formControl.parent.get('nomeFornitoreCombustibile') &&
      this.formControl.parent.get('nomeFornitoreCombustibile').value !== ''
    ) {
      companyName = this.formControl.parent.get('nomeFornitoreCombustibile').value;
    }
    if (this.value && companyName) {
      initialValues.push({_id: this.value, companyName: companyName});
    }

    if (!this.to.minTermLength) {
      this.to.minTermLength = 0;
    }

    this.options$ = concat(
      of(initialValues),
      this.optionsInput$.pipe(
        takeUntil(this.onDestroy$), // auto-unsubscribe on onDestroy()
        debounceTime(500), // max 1 query every 350 milliseconts
        tap(() => this.optionsLoading = true), // Start loader
        distinctUntilChanged(), // Eliminate duplicate values
        switchMap((term: string) => {
          const options: any = {
            general: term,
          };
          if (typeof this.to.getAll !== 'undefined') {
            options.getAll = this.to.getAll;
          }
          if (!this.to.search$) {
            this.optionsLoading = false
            if (options && Array.isArray(options) && options.length) {
              return options;
            }
            return [];
          }
          return this.to.search$(0, 10, '', '', options)
            .pipe(
              map((res: ListApiResponseV2) => {
                if (res.status) {
                  if (res.data.items) {
                    return res.data.items;
                  }
                  if (res.data && Array.isArray(res.data)) {
                    res.data.forEach((data: any) => {
                      const vatNumber = data.vatNumber ? ` - ${data.vatNumber}` : '';
                      const city = (data.legalAddress && data.legalAddress.city) ? ` - ${data.legalAddress.city}` : '';
                      data.shortCompanyName = data.companyName;
                      data.companyName = `${data.companyName}${vatNumber}${city}`;
                    });
                    return res.data;
                  }
                }
                return [];
              }),
              catchError(() => of([])), // empty list on error
              tap(() => this.optionsLoading = false)
            )
        })
      )
    )
  }

  /**
   * Handle afterViewInit of this component
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  /**
   * Handle ngOnDestroy of this component
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.onDestroy$.complete();
  }

  /**
   * Fired every time model data changes
   *
   * @param {*} data new model data
   */
  onChange(data: any): void {
    if (data && data.shortCompanyName) {
      if (this.formControl.parent.get('ragioneSocialeFornitore')) {
        this.formControl.parent.get('ragioneSocialeFornitore').setValue(data.shortCompanyName);
      } else if (this.formControl.parent.get('nomeFornitoreCombustibile')) {
        this.formControl.parent.get('nomeFornitoreCombustibile').setValue(data.shortCompanyName);
      }
    } else if (data && data.companyName) {
      if (this.formControl.parent.get('ragioneSocialeFornitore')) {
        this.formControl.parent.get('ragioneSocialeFornitore').setValue(data.companyName);
      } else if (this.formControl.parent.get('nomeFornitoreCombustibile')) {
        this.formControl.parent.get('nomeFornitoreCombustibile').setValue(data.companyName);
      }
    }

    if (typeof this.to.callback === 'function') {
      this.to.callback(data);
    }
  }
}
